import React, { useState, useEffect, useRef } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useNavigate } from 'react-router-dom';
import emailService from '../../../services/emailService';

const VerificationStep = ({ formData, updateFormData, onPrevious, onSubmit, isSubmitting, submitError }) => {
  const navigate = useNavigate();
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);
  const [isEditingEmail, setIsEditingEmail] = useState(false);

  // Timer for code expiration
  useEffect(() => {
    let interval;
    if (codeSent && remainingTime > 0) {
      interval = setInterval(() => {
        const timeLeft = emailService?.getRemainingTime(formData?.email);
        setRemainingTime(timeLeft);
        
        if (timeLeft === 0) {
          setCanResend(true);
        }
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [codeSent, remainingTime, formData?.email]);

  const handleSendCode = async () => {
    if (!formData?.email) {
      setError('Por favor ingresa tu correo electrónico');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await emailService?.sendVerificationEmail(formData?.email);
      
      if (result?.success) {
        setCodeSent(true);
        setSuccess('Código enviado exitosamente. Revisa la consola del navegador (F12 → Console) para ver tu código de 6 dígitos.');
        console.log('\n⚠️ IMPORTANTE: Busca el código de 6 dígitos en los mensajes de arriba ⬆️\n');
      } else {
        setError(result?.message || 'Error al enviar el código');
      }
    } catch (err) {
      console.error('Error al enviar código:', err);
      setError('Error al enviar el código. Intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setVerificationCode(['', '', '', '', '', '']);
    await handleSendCode();
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/?.test(value)) return;

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs?.current?.[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e?.key === 'Backspace' && !verificationCode?.[index] && index > 0) {
      inputRefs?.current?.[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const fullCode = verificationCode?.join('');
    
    if (fullCode?.length !== 6) {
      setError('Por favor ingresa el código completo de 6 dígitos');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await emailService?.verifyCode(formData?.email, fullCode);
      
      if (result?.success) {
        setSuccess('¡Código verificado correctamente!');
        setTimeout(() => {
          onSubmit?.();
        }, 1000);
      } else {
        setError(result?.message || 'Código incorrecto');
        setVerificationCode(['', '', '', '', '', '']);
        if (inputRefs?.current?.[0]) {
          inputRefs?.current?.[0]?.focus();
        }
      }
    } catch (err) {
      console.error('Error al verificar código:', err);
      setError('Error al verificar el código. Intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const handleComplete = () => {
    if (!formData?.emailVerified) {
      setError('Debes verificar tu correo electrónico antes de continuar');
      return;
    }
    onSubmit?.();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Verificación de Cuenta</h2>
        <p className="text-muted-foreground">Verifica tu correo electrónico para completar el registro</p>
      </div>
      <div className="bg-gradient-to-r from-primary/10 to-accent/10 border-2 border-primary/20 rounded-xl p-6">
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="bg-primary/20 rounded-full p-6">
              <Icon name="Mail" size={48} className="text-primary" />
            </div>
          </div>
          <div>
            <p className="text-lg font-bold text-foreground mb-2">Verifica tu correo electrónico</p>
            <p className="text-muted-foreground">
              Enviaremos un código de verificación a:
            </p>
            <p className="text-lg font-bold text-primary mt-2">{formData?.email}</p>
            <Button
              variant="link"
              size="sm"
              onClick={() => setIsEditingEmail(true)}
              iconName="Edit"
              iconPosition="left"
              className="mt-2"
            >
              Cambiar correo
            </Button>
          </div>
        </div>
      </div>
      {!codeSent ? (
        <Button
          variant="default"
          size="lg"
          fullWidth
          onClick={handleSendCode}
          iconName="Send"
          iconPosition="right"
          className="bg-gradient-to-r from-primary to-accent hover:shadow-lg transform hover:scale-105 transition-all"
        >
          Enviar Código de Verificación
        </Button>
      ) : (
        <div className="space-y-4">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <Icon name="Info" size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">Código enviado exitosamente</p>
                <p>Revisa tu bandeja de entrada y carpeta de spam. El código es válido por 10 minutos.</p>
              </div>
            </div>

            {!formData?.emailVerified && (
              <div className="pt-4 border-t border-blue-200">
                <div className="grid grid-cols-6 gap-2 mb-4">
                  {Array.from({ length: 6 }, (_, index) => (
                    <Input
                      key={index}
                      type="text"
                      maxLength={1}
                      value={verificationCode?.[index]}
                      onChange={(e) => handleCodeChange(index, e?.target?.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      ref={(el) => (inputRefs.current[index] = el)}
                      error={error}
                      className="text-center text-2xl font-bold tracking-widest"
                    />
                  ))}
                </div>

                <Button
                  variant="default"
                  size="lg"
                  fullWidth
                  onClick={handleVerify}
                  iconName="CheckCircle"
                  iconPosition="right"
                  className="mt-4 bg-gradient-to-r from-primary to-accent"
                  disabled={!codeSent || isLoading}
                >
                  {isLoading ? 'Verificando...' : 'Verificar Código'}
                </Button>
              </div>
            )}
          </div>

          {!formData?.emailVerified && (
            <div className="text-center">
              {canResend ? (
                <Button
                  variant="link"
                  onClick={handleResendCode}
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Reenviar código
                </Button>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Podrás reenviar el código en <span className="font-bold text-primary">{formatTime(remainingTime)}</span>
                </p>
              )}
            </div>
          )}
        </div>
      )}
      {formData?.emailVerified && (
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="bg-green-200 rounded-full p-3">
              <Icon name="CheckCircle" size={32} className="text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-lg font-bold text-green-900">¡Correo Verificado!</p>
              <p className="text-sm text-green-700">Tu cuenta ha sido verificada exitosamente</p>
            </div>
          </div>
        </div>
      )}
      <div className="border-t pt-6">
        <h3 className="text-lg font-bold text-foreground mb-4">Resumen de Registro</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-muted-foreground">Nombre</span>
            <span className="font-semibold text-foreground">{formData?.fullName}</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-muted-foreground">Correo</span>
            <span className="font-semibold text-foreground">{formData?.email}</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-muted-foreground">Teléfono</span>
            <span className="font-semibold text-foreground">{formData?.phone}</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-muted-foreground">Plan Seleccionado</span>
            <span className="font-semibold text-primary">{formData?.selectedPlan?.name}</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-muted-foreground">Método de Pago</span>
            <span className="font-semibold text-foreground capitalize">
              {formData?.paymentMethod}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-muted-foreground">Estado de Verificación</span>
            <span className={`font-semibold ${formData?.emailVerified ? 'text-green-600' : 'text-yellow-600'}`}>
              {formData?.emailVerified ? '✓ Verificado' : 'Pendiente'}
            </span>
          </div>
        </div>
      </div>
      <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Icon name="Clock" size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-yellow-900">
            <p className="font-semibold mb-1">Proceso de Activación</p>
            <p>Tu cuenta será activada una vez que nuestro equipo verifique tu comprobante de pago. Esto puede tomar entre 24-48 horas hábiles. Te notificaremos por correo electrónico.</p>
          </div>
        </div>
      </div>
      {submitError && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-900">
              <p className="font-semibold mb-1">Error al completar el registro</p>
              <p>{submitError}</p>
            </div>
          </div>
        </div>
      )}
      <div className="flex gap-4 pt-6 border-t">
        <Button
          variant="outline"
          size="lg"
          onClick={onPrevious}
          iconName="ArrowLeft"
          iconPosition="left"
          className="flex-1"
          disabled={isSubmitting}
        >
          Regresar
        </Button>

        <Button
          variant="default"
          size="lg"
          onClick={handleComplete}
          iconName={isSubmitting ? "Loader2" : "CheckCircle"}
          iconPosition="right"
          className="flex-1 bg-gradient-to-r from-primary to-accent hover:shadow-lg transform hover:scale-105 transition-all"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Registrando...' : 'Completar Registro'}
        </Button>
      </div>
    </div>
  );
};

export default VerificationStep;